<script lang="ts">
  import BaseScene from "@engine/BaseScene.svelte";
  import { IMPRINT_ATTRIBUTION, LEGAL_OWNER } from "@config/constants/branding";
  import aiDisclosure from "@config/content/ai_disclosure.json";
  import commitmentFallback from "@config/content/commitment_fallback.json";
  import gameInfo from "@config/game_info.json";

  export let onBack: () => void;
  export let commitmentLive: { text: string; lastUpdated: string; changelogUrl: string } | null = null;

  let tab: "commitment" | "credits" = "commitment";

  $: commitment = commitmentLive ?? commitmentFallback;
</script>

<BaseScene sceneId="ethical-monetization" ariaLabel="About the Studio" onEscape={onBack}>
  <div class="em">
    <h1>About the Studio</h1>

    <div class="em__tabs" role="tablist">
      <button type="button" role="tab" aria-selected={tab === "commitment"} on:click={() => (tab = "commitment")}>Commitment</button>
      <button type="button" role="tab" aria-selected={tab === "credits"} on:click={() => (tab = "credits")}>Studio and Credits</button>
    </div>

    {#if tab === "commitment"}
      <section aria-label="Commitment">
        <p>{commitment.text}</p>
        <p class="em__updated">Last updated: {commitment.lastUpdated}</p>
        <p>
          <a href={commitment.changelogUrl} target="_blank" rel="noopener noreferrer">
            Read the changelog
          </a>
        </p>
      </section>
    {:else}
      <section aria-label="Credits">
        <h2>Studio</h2>
        <p>{IMPRINT_ATTRIBUTION}</p>
        <p>Published by {LEGAL_OWNER}</p>

        <h2>Accessibility</h2>
        <p>
          {gameInfo.brand} {gameInfo.productSubtitle} follows the Cinderpoint Accessible Reading Architecture Standard (CARAS)
          and the Software Accessibility Standard. WCAG AA is the minimum bar.
        </p>

        <h2>Credits</h2>
        <ul>
          <li>Game design + code: Waydell D. Carvalho</li>
          <li>Science advisory: sourced from NOAA, Smithsonian Ocean Portal, MBARI, and peer-reviewed research</li>
          <li>Typeface: Atkinson Hyperlegible (Braille Institute), self-hosted</li>
          <li>Audio: Howler.js</li>
        </ul>

        <h2>AI Disclosure</h2>
        <p class="em__ai">{aiDisclosure.paragraph}</p>
      </section>
    {/if}

    <button type="button" class="em__back" on:click={onBack}>Back</button>
  </div>
</BaseScene>

<style>
  .em {
    max-width: 720px;
    margin: 0 auto;
    padding: 2rem 1rem;
  }
  .em h1 {
    color: var(--color-heading);
    text-align: center;
    margin-bottom: 1.5rem;
  }
  .em__tabs {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    border-bottom: 1px solid var(--color-divider);
  }
  .em__tabs button {
    padding: 0.75em 1.5em;
    background: transparent;
    border: none;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    font-size: 1rem;
    min-height: 44px;
  }
  .em__tabs button[aria-selected="true"] {
    border-bottom-color: var(--color-accent);
    color: var(--color-accent);
    font-weight: 600;
  }
  .em h2 {
    color: var(--color-heading);
    font-size: 1.15rem;
    margin: 1rem 0 0.5rem;
  }
  .em p, .em li {
    text-align: left;
  }
  .em__updated {
    color: var(--color-muted);
    font-size: 0.9rem;
  }
  .em__ai {
    padding: 0.75rem;
    background: var(--color-surface);
    border-left: 4px solid var(--color-accent);
    border-radius: 0.25em;
  }
  .em__back {
    display: block;
    margin: 2rem auto 0;
    min-height: 44px;
    padding: 0.5em 1.5em;
    background: var(--color-surface);
    border: 1px solid var(--color-divider);
    border-radius: 0.5em;
    cursor: pointer;
  }
</style>
